import React from 'react';
import {
    Grid,
    Typography,
    Fade,
    SxProps,
    CircularProgress,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { styles } from './create-form-feature-dialog-helper';
import { Button, Dialog, IconButton, LoadingButton } from '@biq/apex-ui';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { themeObject } from '../../lib';

interface CreateFormFeatureDialogProps {
    children: React.ReactNode;
    handleSubmit: () => void;
    handleCloseButton: () => void;
    handleBackButton: () => void;
    buttonText: string;
    displaySubmitButton?: boolean;
    displayBackButton?: boolean;
    customStyles?: SxProps;
    isLoading?: boolean;
    listConfig: {
        [key: string]: {
            imgSrc: string;
            headingText?: string;
            iconStyles?: React.CSSProperties;
        };
    };
    customButtonContainerStyles?: SxProps;
    isOpenInModal?: boolean;
}

export const CreateFormFeatureDialog = ({
    children,
    handleSubmit,
    handleCloseButton,
    handleBackButton,
    buttonText,
    displaySubmitButton = true,
    customStyles,
    isLoading = false,
    listConfig,
    customButtonContainerStyles,
    isOpenInModal = false,
    displayBackButton = true,
}: CreateFormFeatureDialogProps): React.JSX.Element => {
    const location = useLocation();
    const pathnameArr = location.pathname.split('/');
    let featureType;

    if (isOpenInModal) {
        featureType = Object.keys(listConfig)[0];
    } else if (pathnameArr.length > 3) {
        featureType = pathnameArr.at(-1);
    } else {
        featureType = pathnameArr[1];
    }

    return (
        <Dialog
            sx={customStyles ?? styles.root}
            disableRestoreFocus
            open={true}
            closable={false}
            fullWidth
            TransitionComponent={Fade}
            TransitionProps={{ timeout: 0 }}
        >
            <Grid sx={styles.container}>
                <Grid sx={styles.headerContainer}>
                    <Grid
                        sx={{
                            visibility: displayBackButton
                                ? 'visible'
                                : 'hidden',
                        }}
                    >
                        <Button
                            size='medium'
                            variant='text'
                            startIcon={
                                <ArrowBackIcon
                                    color={themeObject.palette.primary[400]}
                                />
                            }
                            onClick={handleBackButton}
                        >
                            Back
                        </Button>
                    </Grid>
                    <Grid>
                        <IconButton
                            buttonType='closeButton'
                            size='small'
                            onClick={handleCloseButton}
                            sx={styles.closeButtonStyles}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Grid>
                </Grid>
                {featureType && listConfig[featureType] && (
                    <Grid sx={styles.bodyContainer}>
                        <Grid>
                            <img
                                src={listConfig[featureType].imgSrc}
                                alt='job-icon'
                                style={listConfig[featureType]?.iconStyles}
                            />
                        </Grid>
                        {listConfig[featureType].headingText && (
                            <Grid mt={1.5} className='headingText'>
                                <Typography sx={styles.headingText}>
                                    {listConfig[featureType].headingText}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                )}
                {children}
                {displaySubmitButton && (
                    <Grid
                        sx={{
                            ...styles.buttonContainer,
                            ...customButtonContainerStyles,
                        }}
                    >
                        <LoadingButton
                            size='small'
                            onClick={handleSubmit}
                            disabled={isLoading}
                            color='primary'
                            loading={isLoading}
                            variant='contained'
                            position='end'
                            sx={styles.buttonStyles}
                            loadingVariant='withoutIcon'
                            loaderIcon={
                                <CircularProgress
                                    variant='indeterminate'
                                    disableShrink
                                />
                            }
                        >
                            {buttonText}
                        </LoadingButton>
                    </Grid>
                )}
            </Grid>
        </Dialog>
    );
};
